const { gsap } = window

if (gsap) {
  gsap.config({ nullTargetWarn: false })
  gsap.defaults({ ease: 'none' })
}

const MotionTokens = {
  goldenRatio: 1.618,
  primaryEase: 'expo.out'
}

export const durationDefault = 1 / MotionTokens.goldenRatio
export const staggerDefault = 0.1 * MotionTokens.goldenRatio
export const animPrimary = MotionTokens.primaryEase

const gsapPluginAssetMap = getPluginAssetMap()

const gsapPluginFiles = {
  CustomEase: gsapPluginAssetMap.CustomEase || 'custom-ease.min.js',
  Draggable: gsapPluginAssetMap.Draggable || 'draggable.min.js',
  DrawSVGPlugin: gsapPluginAssetMap.DrawSVGPlugin || 'draw-svg-plugin.min.js',
  InertiaPlugin: gsapPluginAssetMap.InertiaPlugin || 'inertia-plugin.min.js',
  ScrollTrigger: gsapPluginAssetMap.ScrollTrigger || 'scrolltrigger.min.js',
  SplitText: gsapPluginAssetMap.SplitText || 'split-text.min.js'
}

const gsapPluginPromises = new Map()
let primaryEaseRegistered = false

const noop = () => undefined

function getPluginAssetMap() {
  const source = document.querySelector(
    '[data-gsap-plugin-assets]'
  )?.textContent
  if (!source) return {}

  try {
    const parsed = JSON.parse(source)
    return parsed && typeof parsed === 'object' ? parsed : {}
  } catch {
    return {}
  }
}

function normalizePlugins(plugins) {
  if (plugins == null) return []
  if (typeof plugins === 'string') return [plugins]
  if (Array.isArray(plugins)) return plugins

  throw new TypeError(
    `normalizePlugins: plugins must be a string or array of plugin names; received ${plugins}`
  )
}

function registerPrimaryEase() {
  if (primaryEaseRegistered || !globalThis.CustomEase) return
  globalThis.CustomEase.create('primary-ease', '0.625, 0.05, 0, 1')
  primaryEaseRegistered = true
}

function loadPluginScript(pluginName, filename) {
  if (globalThis[pluginName]) return Promise.resolve(globalThis[pluginName])
  if (gsapPluginPromises.has(pluginName)) {
    return gsapPluginPromises.get(pluginName)
  }

  const pluginUrl =
    filename.startsWith('/') ||
    filename.startsWith('//') ||
    /^[a-z]+:/i.test(filename)
      ? filename
      : new URL(filename, import.meta.url).href
  const promise = new Promise((resolve, reject) => {
    const script = document.createElement('script')
    script.src = pluginUrl
    script.defer = true
    script.dataset.gsapPlugin = pluginName
    script.onload = () => resolve(globalThis[pluginName])
    script.onerror = () =>
      reject(
        new Error(
          `loadPluginScript: failed to load ${pluginUrl}; expected globalThis.${pluginName}`
        )
      )
    document.head.append(script)
  }).catch((error) => {
    gsapPluginPromises.delete(pluginName)
    throw error
  })

  gsapPluginPromises.set(pluginName, promise)
  return promise
}

async function loadPlugin(pluginName) {
  if (typeof pluginName !== 'string' || !gsapPluginFiles[pluginName]) {
    throw new TypeError(
      `loadPlugin: pluginName must be one of ${Object.keys(
        gsapPluginFiles
      ).join(', ')}; received ${pluginName}`
    )
  }

  const filename = gsapPluginFiles[pluginName]

  try {
    const plugin = await loadPluginScript(pluginName, filename)
    if (!plugin) {
      return null
    }

    gsap.registerPlugin(plugin)
    if (pluginName === 'CustomEase') registerPrimaryEase()
    return plugin
  } catch {
    return null
  }
}

export async function loadGsapPlugins(plugins = []) {
  if (!gsap) return null

  const pluginNames = normalizePlugins(plugins)
  await Promise.all(pluginNames.map((pluginName) => loadPlugin(pluginName)))
  return gsap
}

export async function useGsap(host, setup = noop, plugins = []) {
  if (!(host instanceof HTMLElement)) {
    throw new TypeError(
      `useGsap: host must be an HTMLElement; received ${host}`
    )
  }

  if (!gsap) return null

  await loadGsapPlugins(plugins)

  const context = gsap.context(setup, host)
  return { context, contextSafe: context.add.bind(context), gsap }
}
